import React, { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowBigUp, ArrowBigDown, MessageSquare } from "lucide-react";

const Threads = ({ user }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [sortBy, setSortBy] = useState("hot");
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    image_url: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchThreads();
  }, [sortBy]);

  const fetchThreads = async () => {
    try {
      const response = await axiosInstance.get(`/threads?sort=${sortBy}`);
      setThreads(response.data);
    } catch (error) {
      toast.error("Failed to load threads");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/threads", formData);
      toast.success("Thread created successfully!");
      setCreateOpen(false);
      setFormData({ title: "", content: "", image_url: "" });
      fetchThreads();
    } catch (error) {
      toast.error("Failed to create thread");
    }
  };

  const handleVote = async (threadId, voteType, e) => {
    e.stopPropagation();
    try {
      await axiosInstance.post(`/threads/${threadId}/vote`, { vote_type: voteType });
      fetchThreads();
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleDelete = async (threadId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this thread?")) return;
    try {
      await axiosInstance.delete(`/threads/${threadId}`);
      toast.success("Thread deleted");
      fetchThreads();
    } catch (error) {
      toast.error("Failed to delete thread");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div data-testid="threads-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>Discussions</h1>
        <div className="flex gap-2">
          <div className="flex gap-1 bg-white rounded-full p-1 border border-orange-200">
            <Button 
              data-testid="sort-hot"
              variant={sortBy === "hot" ? "default" : "ghost"}
              size="sm"
              className={`rounded-full ${sortBy === "hot" ? "bg-orange-500 text-white" : ""}`}
              onClick={() => setSortBy("hot")}
            >
              Hot
            </Button>
            <Button 
              data-testid="sort-new"
              variant={sortBy === "new" ? "default" : "ghost"}
              size="sm"
              className={`rounded-full ${sortBy === "new" ? "bg-orange-500 text-white" : ""}`}
              onClick={() => setSortBy("new")}
            >
              New
            </Button>
            <Button 
              data-testid="sort-top"
              variant={sortBy === "top" ? "default" : "ghost"}
              size="sm"
              className={`rounded-full ${sortBy === "top" ? "bg-orange-500 text-white" : ""}`}
              onClick={() => setSortBy("top")}
            >
              Top
            </Button>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-thread-button" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6">
                New Thread
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Thread</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    data-testid="thread-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    data-testid="thread-content-input"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    rows={8}
                    required
                  />
                </div>
                <div>
                  <Label>Image (Optional)</Label>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="thread_image_file" className="text-sm text-gray-600">Upload from device</Label>
                      <Input
                        id="thread_image_file"
                        data-testid="thread-image-file-input"
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error("Image must be less than 2MB");
                              e.target.value = "";
                              return;
                            }
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setFormData({...formData, image_url: reader.result});
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max 2MB</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="text-xs text-gray-500">OR</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                    <div>
                      <Label htmlFor="image_url" className="text-sm text-gray-600">Paste image URL</Label>
                      <Input
                        id="image_url"
                        data-testid="thread-image-url-input"
                        type="url"
                        value={formData.image_url?.startsWith('data:') ? '' : formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    {formData.image_url && (
                      <div className="mt-2">
                        <img src={formData.image_url} alt="Preview" className="max-h-32 rounded border" />
                      </div>
                    )}
                  </div>
                </div>
                <Button data-testid="submit-thread-button" type="submit" className="w-full bg-orange-500 hover:bg-orange-600">Create Thread</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {threads.map((thread) => (
          <Card 
            key={thread.id} 
            data-testid={`thread-card-${thread.id}`}
            className="bg-white/90 backdrop-blur-sm border-orange-100 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate(`/app/threads/${thread.id}`)}
          >
            <div className="flex">
              <div className="flex flex-col items-center p-4 gap-1 bg-gray-50 rounded-l-lg">
                <Button 
                  data-testid={`upvote-thread-${thread.id}`}
                  variant="ghost" 
                  size="sm" 
                  className="p-1 h-auto hover:bg-orange-100"
                  onClick={(e) => handleVote(thread.id, "up", e)}
                >
                  <ArrowBigUp className="w-6 h-6 text-gray-600 hover:text-orange-500" />
                </Button>
                <span className="text-sm font-bold text-gray-700">
                  {thread.upvotes - thread.downvotes}
                </span>
                <Button 
                  data-testid={`downvote-thread-${thread.id}`}
                  variant="ghost" 
                  size="sm" 
                  className="p-1 h-auto hover:bg-blue-100"
                  onClick={(e) => handleVote(thread.id, "down", e)}
                >
                  <ArrowBigDown className="w-6 h-6 text-gray-600 hover:text-blue-500" />
                </Button>
              </div>

              <div className="flex-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl" style={{ fontFamily: 'Merriweather, serif' }}>
                    {thread.title}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={thread.author_picture} />
                      <AvatarFallback>{thread.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span>{thread.author_name}</span>
                    <span>•</span>
                    <span>{new Date(thread.created_at).toLocaleString()}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  {thread.image_url && (
                    <div className="mb-3 rounded-lg overflow-hidden">
                      <img src={thread.image_url} alt={thread.title} className="w-full max-h-64 object-cover" />
                    </div>
                  )}
                  <p className="text-gray-600 line-clamp-2 mb-3">{thread.content}</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MessageSquare className="w-4 h-4" />
                      <span>{thread.comment_count} comments</span>
                    </div>
                    {(user.id === thread.author_id || user.is_admin) && (
                      <Button 
                        data-testid={`delete-thread-${thread.id}`}
                        variant="destructive" 
                        size="sm"
                        onClick={(e) => handleDelete(thread.id, e)}
                        className="rounded-full"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {threads.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No threads yet. Start a discussion!</p>
        </div>
      )}
    </div>
  );
};

export default Threads;